// frontend/src/components/EnhancedAddDataModal.js
// Enhanced "+Add Data" modal with universal parameter selection and shopping cart interface

import React, { useState, useCallback } from 'react';
import {
  Modal,
  Steps,
  Card,
  Button,
  Space,
  Typography,
  Alert,
  Row,
  Col
} from 'antd';
import {
  SearchOutlined,
  ShoppingCartOutlined,
  ArrowRightOutlined
} from '@ant-design/icons';
import UniversalParameterSelector from './UniversalParameterSelector';
import UniversalDataEntryForm from './UniversalDataEntryForm';

const { Step } = Steps;
const { Title, Text } = Typography;

/**
 * Enhanced Add Data Modal Component
 * Provides streamlined workflow for parameter selection and data entry
 */
const EnhancedAddDataModal = ({
  visible,
  onClose,
  onSave,
  patientId,
  loading = false,
  title = "Add Patient Data"
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedParameters, setSelectedParameters] = useState([]);

  // Handle parameter selection changes
  const handleParameterSelectionChange = useCallback((parameters) => {
    setSelectedParameters(parameters);
  }, []);

  // Handle proceeding to data entry step
  const handleProceedToDataEntry = useCallback((parameters) => {
    if (parameters.length === 0) {
      return;
    }
    setSelectedParameters(parameters);
    setCurrentStep(1);
  }, []);

  // Handle going back to parameter selection
  const handleBackToSelection = useCallback(() => {
    setCurrentStep(0);
  }, []);

  // Handle data save
  const handleDataSave = useCallback(async (dataPayload) => {
    try {
      await onSave(dataPayload);
      // Reset modal state after successful save
      setCurrentStep(0);
      setSelectedParameters([]);
      onClose();
    } catch (error) {
      console.error('Error saving data:', error);
      // Error handling is done in the form component
    }
  }, [onSave, onClose]);

  // Handle modal close
  const handleModalClose = useCallback(() => {
    // Reset state when closing
    setCurrentStep(0);
    setSelectedParameters([]);
    onClose();
  }, [onClose]);

  // Get step content
  const getStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <UniversalParameterSelector
            selectedParameters={selectedParameters}
            onParameterSelectionChange={handleParameterSelectionChange}
            onProceedToDataEntry={handleProceedToDataEntry}
            maxSelections={20}
          />
        );
      case 1:
        return (
          <UniversalDataEntryForm
            selectedParameters={selectedParameters}
            onSave={handleDataSave}
            onCancel={handleModalClose}
            onBack={handleBackToSelection}
            patientId={patientId}
            loading={loading}
          />
        );
      default:
        return null;
    }
  };

  // Get modal width based on current step
  const getModalWidth = () => {
    switch (currentStep) {
      case 0:
        return 900; // Wider for parameter selection
      case 1:
        return 1200; // Widest for data entry form
      default:
        return 800;
    }
  };

  // Get modal footer
  const getModalFooter = () => {
    if (currentStep === 1) {
      // Data entry step - footer handled by form component
      return null;
    }

    // Parameter selection step
    return [
      <Button key="cancel" onClick={handleModalClose}>
        Cancel
      </Button>,
      <Button 
        key="proceed" 
        type="primary"
        icon={<ArrowRightOutlined />}
        disabled={selectedParameters.length === 0}
        onClick={() => handleProceedToDataEntry(selectedParameters)}
      >
        Proceed to Data Entry ({selectedParameters.length})
      </Button>
    ];
  };

  return (
    <Modal
      title={
        <Space>
          <Title level={4} style={{ margin: 0 }}>
            {title}
          </Title>
          {selectedParameters.length > 0 && (
            <Text type="secondary">
              - {selectedParameters.length} parameter(s) selected
            </Text>
          )}
        </Space>
      }
      open={visible}
      onCancel={handleModalClose}
      footer={getModalFooter()}
      width={getModalWidth()}
      destroyOnClose
      maskClosable={false}
      style={{ top: 20 }}
    >
      {/* Step Content */}
      <div style={{ minHeight: 400 }}>
        {getStepContent()}
      </div>
    </Modal>
  );
};

export default EnhancedAddDataModal;
